import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { User } from '../../types/User'
import { Tribe } from '../../types/Tribe'
import { useNavigate } from 'react-router'
import { useLazyGetAllUsersQuery } from '../../redux/user.endpoints'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faPlus,
  faUsers,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons'
import { ArrowsDownUp, PencilSimple } from '@phosphor-icons/react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { parseTribes } from '../../types/Tribe'
import { useLazyGetAllRolesQuery } from '../../redux/role.endpoints'
import { Role } from '../../types/Roles'
import { useLazyGetAllTribesQuery } from '../../redux/tribe.endpoints'
import { useLazyGetAllTeamsQuery } from '../../redux/team.endpoints'
import { SelectOption } from '../../types/SelectOption'

interface Props {
  companyId: number
}

type SortColumn = 'name' | 'email' | 'guild' | 'tribe' | 'status'

const ALL = ''

const EmployeesView = ({ companyId }: Props) => {
  const navigate = useNavigate()
  const [employees, setEmployees] = useState([] as User[])
  const [searchQuery, setSearchQuery] = useState('')
  const [getAllRoles, getAllRolesResponse] = useLazyGetAllRolesQuery()
  const [tribes, setTribes] = useState([] as Tribe[])
  const [getTribes, { data: tribesData }] = useLazyGetAllTribesQuery()
  const [availableTeams, setAvailableTeams] = useState<SelectOption[]>([])
  const [getAllTeams, { data: teamsData }] = useLazyGetAllTeamsQuery()
  const [getUsers, { data }] = useLazyGetAllUsersQuery()
  const [guilds, setGuilds] = useState<Array<Role>>([])
  const [selectedTeam, setSelectedTeam] = useState(ALL)
  const [selectedStatus, setSelectedStatus] = useState(ALL)
  const [selectedGuild, setSelectedGuild] = useState(ALL)
  const [selectedTribe, setSelectedTribe] = useState(ALL)

  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  useEffect(() => {
    setTribes(tribesData || [])
  }, [tribesData])

  useEffect(() => {
    if (teamsData) {
      const teamOptions = teamsData.map(team => ({
        value: team.id.toString(),
        label: team.name,
      }))
      setAvailableTeams(teamOptions)
    }
  }, [teamsData])

  useEffect(() => {
    getAllTeams(companyId)
  }, [getAllTeams, companyId])

  useEffect(() => {
    setEmployees(data || [])
  }, [data])

  useEffect(() => {
    getAllRoles()
  }, [])

  useEffect(() => {
    getUsers({
      companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
      isActive: false,
    })
  }, [])

  useEffect(() => {
    if (getAllRolesResponse && getAllRolesResponse.data) {
      setGuilds(getAllRolesResponse.data)
    }
  }, [getAllRolesResponse])

  useEffect(() => {
    getTribes({
      companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
  }, [])

  const getStatus = date => {
    if (!date) return 'Active'
    const today = new Date()
    const userEndDate = new Date(date)

    if (userEndDate < today) return 'Inactive'
    else return 'Active'
  }

  const visibleEmployees = [...employees]
    .filter(employee => {
      const fullName =
        `${employee.firstName} ${employee.lastName}`.toLowerCase()
      return fullName.includes(searchQuery.toLowerCase())
    })
    .filter(employee => {
      if (selectedGuild) {
        return employee.guildId.toString() === selectedGuild
      }
      return true
    })
    .filter(employee => {
      if (selectedTribe) {
        return employee.tribes.some(
          tribe => tribe.id.toString() === selectedTribe,
        )
      }
      return true
    })
    .filter(employee => {
      if (selectedTeam) {
        return employee.teamAllocations?.some(
          allocation => allocation.team?.id.toString() === selectedTeam,
        )
      }
      return true
    })
    .filter(employee => {
      if (selectedStatus === 'active') {
        return getStatus(employee.end_on) === 'Active'
      } else if (selectedStatus === 'inactive') {
        return getStatus(employee.end_on) === 'Inactive'
      }
      return true
    })
    .sort((a, b) => {
      let sortValueA = ''
      let sortValueB = ''
      if (sortColumn === 'name') {
        sortValueA = `${a.firstName} ${a.lastName}`
        sortValueB = `${b.firstName} ${b.lastName}`
      } else if (sortColumn === 'email') {
        sortValueA = a.email
        sortValueB = b.email
      } else if (sortColumn === 'status') {
        sortValueA = getStatus(a.end_on)
        sortValueB = getStatus(b.end_on)
      } else if (sortColumn === 'guild') {
        sortValueA = guilds.find(guild => guild.id === a.guildId)?.role ?? ''
        sortValueB = guilds.find(guild => guild.id === b.guildId)?.role ?? ''
      } else if (sortColumn === 'tribe') {
        sortValueA = parseTribes(a.tribes)
        sortValueB = parseTribes(b.tribes)
      }
      return sortOrder === 'asc'
        ? sortValueA.localeCompare(sortValueB)
        : sortValueB.localeCompare(sortValueA)
    })

  const sortableHeader = (label: string, column: SortColumn, width: string) => (
    <th
      className="sortable"
      style={{ width }}
      onClick={() => handleSort(column)}
    >
      {label}
      <ArrowsDownUp className="tm-sort-icon" size={14} weight="regular" />
    </th>
  )

  return (
    <div className="page-content team-members-page">
      <Helmet title="Time Off - Members" />
      <div className="team-members-page__header">
        <h1 className="team-members-page__title">Manage Members</h1>
        <button
          className="tm-add-button"
          onClick={() => navigate('/members/new')}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add New Member
        </button>
      </div>
      <div className="team-members-page__filters">
        <div className="team-members-page__search">
          <input
            type="text"
            className="team-members-page__search-input"
            placeholder="Search by Name"
            aria-label="Search by Name"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <FontAwesomeIcon
            className="team-members-page__search-icon"
            icon={faSearch}
          />
        </div>
        <div className="team-members-page__filter-row">
          <div className="tm-filter">
            <span className="tm-filter__label">Guild</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedGuild}
                onChange={e => setSelectedGuild(e.target.value)}
              >
                <option value={ALL}>All Guilds</option>
                {guilds.map(guild => (
                  <option key={guild.id} value={guild.id.toString()}>
                    {guild.role}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <div className="tm-filter">
            <span className="tm-filter__label">Tribe</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedTribe}
                onChange={e => setSelectedTribe(e.target.value)}
              >
                <option value={ALL}>All Tribes</option>
                {tribes.map(tribe => (
                  <option key={tribe.id} value={tribe.id.toString()}>
                    {tribe.name}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <div className="tm-filter">
            <span className="tm-filter__label">Team</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedTeam}
                onChange={e => setSelectedTeam(e.target.value)}
              >
                <option value={ALL}>All Teams</option>
                {availableTeams.map(team => (
                  <option key={team.value} value={team.value}>
                    {team.label}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <div className="tm-filter">
            <span className="tm-filter__label">Status</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedStatus}
                onChange={e => setSelectedStatus(e.target.value)}
              >
                <option value={ALL}>All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="tm-table-wrap">
        <table className="tm-table">
          <thead>
            <tr>
              {sortableHeader('Name', 'name', '20%')}
              {sortableHeader('Email', 'email', '29%')}
              {sortableHeader('Guild', 'guild', '18%')}
              {sortableHeader('Tribe', 'tribe', '13%')}
              <th style={{ width: '11%' }}>Team</th>
              {sortableHeader('Status', 'status', '9%')}
              <th className="tm-table__edit-cell">{/* Edit */}</th>
            </tr>
          </thead>
          <tbody>
            {visibleEmployees.map(employee => {
              const userTribes = parseTribes(employee.tribes)
              const userTeams =
                employee.teamAllocations
                  ?.map(allocation => allocation.team?.name || '')
                  .filter(val => !!val)
                  .join(', ') || ''
              const status = getStatus(employee.end_on)
              return (
                <tr key={employee.id}>
                  <td className="tm-table__truncate">
                    {employee.firstName} {employee.lastName}
                  </td>
                  <td className="tm-table__truncate">{employee.email}</td>
                  <td className="tm-table__truncate">
                    {guilds.find(guild => guild.id === employee.guildId)
                      ?.role ?? employee.guildId}
                  </td>
                  <td className="tm-table__truncate">{userTribes}</td>
                  <td className="tm-table__truncate">{userTeams}</td>
                  <td>
                    <span
                      className={`tm-status-badge ${
                        status === 'Active'
                          ? 'tm-status-badge--active'
                          : 'tm-status-badge--inactive'
                      }`}
                    >
                      {status}
                    </span>
                  </td>
                  <td className="tm-table__edit-cell">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`edit-tooltip-${employee.id}`}>
                          Edit
                        </Tooltip>
                      }
                    >
                      <button
                        type="button"
                        aria-label="Edit member"
                        className="tm-table__edit-btn"
                        onClick={() => navigate(`/members/${employee.id}`)}
                      >
                        <PencilSimple size={18} weight="regular" />
                      </button>
                    </OverlayTrigger>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {visibleEmployees.length === 0 && (
          <div className="tm-empty-state">
            <FontAwesomeIcon icon={faUsers} size="2x" />
            <span>No members found. Try adjusting your search or filters.</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmployeesView
